import { gql } from "@apollo/client";

/**
 * Bans or un-bans one member.
 *
 * `banned` is **the state to set, not a flip**, so a double-tapped button or a
 * retried request is a no-op that returns the current row rather than landing on
 * the opposite state.
 *
 * Idempotent to the point of doing nothing: the server returns early when the
 * account already has the requested state, so re-banning somebody does **not**
 * re-stamp `bannedAt`, and does not repair a row whose `isDeleted` was cleared
 * by hand. Lifting and re-applying the ban is the only way to put that back —
 * see `isHalfApplied`.
 *
 * `reason` is stored on the account and is what support reads later. It is only
 * meaningful when banning; un-banning clears it along with the other two audit
 * fields, so this screen does not send one then.
 *
 * `bannedBy` is filled in from the admin's own token — never send it.
 *
 * Errors, all as HTTP 200 with an `errors` array (never branch on status):
 * `ACCOUNT_NOT_FOUND` (no member with that id), `BAD_REQUEST` (banning your own
 * account, which the server refuses because the ban would take effect on your
 * next request and lock you out of the mutation that lifts it), `FORBIDDEN`
 * (missing or non-admin token). See `describeBanFailure`.
 *
 * `warning` is the field this mutation exists to return alongside the row: it is
 * set when the outcome was not the plain one — an account the member had already
 * deleted, where a ban locks nothing out and an un-ban restores nothing. The
 * toast prefers it over any wording of our own.
 */
export const SET_USER_BANNED = gql`
  mutation SetUserBanned($userId: Int!, $banned: Boolean!, $reason: String) {
    setUserBanned(userId: $userId, banned: $banned, reason: $reason) {
      userId
      username
      email
      phone
      isBanned
      isDeleted
      isAnonymized
      bannedAt
      banReason
      bannedBy
      warning
    }
  }
`;

/**
 * The feature switch behind `BanGuard`, fleet-wide within ~30s, no deploy.
 *
 * **Not an amnesty.** A ban also marks the account deleted, and sign-in, the OTP
 * paths and re-registration all key off that flag — none of them consult this
 * row, because they are the same checks that keep a *self*-deleted account out.
 * With this off, a banned member still cannot sign in and still cannot register
 * their old number; what stops is the global guard that refuses them on
 * unguarded and public operations.
 *
 * So it is the escape hatch for the guard, not a way to give access back. To do
 * that, un-ban the person.
 */
export const SET_USER_BAN_ENABLED = gql`
  mutation SetUserBanEnabled($enabled: Boolean!) {
    setUserBanEnabled(enabled: $enabled) {
      enabled
      bannedCount
      updatedAt
      usingDefault
    }
  }
`;
